import { Link, useNavigate } from "react-router-dom";
import React, {
  useContext,
  useState,
  useRef,
  useEffect,
  Fragment,
} from "react";
import { AuthContext } from "./context/AuthContext";
import {
  Menu,
  Transition,
  MenuButton,
  MenuItems,
  MenuItem,
} from "@headlessui/react";
import { usePremiumNavigation } from "./utils/premiumNavigation";

function Navbar() {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const { logout, userId, firstName } = useContext(AuthContext);
  const { getPremiumLink } = usePremiumNavigation();

  const handleLogout = async () => {
    localStorage.clear();
    await logout();
    navigate("/");
  };

  useEffect(() => {
    // Handler to close menu when clicking outside
    function handleClickOutside(event) {
      if (
        isMenuOpen &&
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsMenuOpen(false);
      }
    }

    // Add event listener
    document.addEventListener("mousedown", handleClickOutside);

    // Cleanup
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMenuOpen]);

  const navItems = [
    {
      to: "/",
      label: "Home",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
          />
        </svg>
      ),
    },
    {
      to: "/learnsql/1",
      label: "Learn SQL",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
          />
        </svg>
      ),
    },
    {
      to: "/question/1",
      label: "Practice SQL",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
          />
        </svg>
      ),
    },
    {
      to: getPremiumLink(),
      label: "Premium",
      icon: (
        <svg
          className="w-5 h-5 text-amber-500 group-hover:text-amber-600 transition-colors"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      ),
      isPremium: true,
    },
    {
      to: "/pricing",
      label: "Pricing",
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
    // {
    //   to: "https://sqlguroo.com/blog",
    //   label: "Blog",
    //   icon: (
    //     <svg
    //       className="w-5 h-5"
    //       fill="none"
    //       stroke="currentColor"
    //       viewBox="0 0 24 24"
    //     >
    //       <path
    //         strokeLinecap="round"
    //         strokeLinejoin="round"
    //         strokeWidth="2"
    //         d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9.057M13 13h2.5M13 17h2.5"
    //       />
    //     </svg>
    //   ),
    // },
  ];

  return (
    <nav className="fixed top-3 w-full px-2 sm:px-4 z-50">
      <div className="max-w-6xl mx-auto">
        {/* Main Navbar Container */}
        <div className="group relative flex items-center justify-between rounded-3xl border border-slate-200 bg-slate-100/60 backdrop-blur-lg shadow-lg shadow-black/5 py-2">
          {/* Logo */}
          <div className="flex items-center pl-4 md:pl-6">
            <Link to="/" className="flex-shrink-0">
              <div className="flex items-center text-5xl font-extrabold">
                SQL
                <div className="relative bg-blue-100 text-blue-800 text-xl font-semibold me-2 px-2.5 py-0.5 rounded ms-2 flex items-center">
                  Guroo
                  {firstName && (
                    <span className="ml-2 text-blue-500 font-normal">
                      | {firstName}
                    </span>
                  )}
                  <div className="absolute -top-3 -right-3">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-8 h-8"
                      viewBox="0 0 24 24"
                      style={{
                        transform: "rotate(25deg)",
                        color: "#1a1a1a", // Darker color
                      }}
                    >
                      {/* Main cap */}
                      <path
                        fill="currentColor"
                        d="M12 2L1 7l11 5 11-5-11-5zm0 6.5l-8-3.64V15l8 3.64 8-3.64V4.86l-8 3.64z"
                      />
                      {/* Tassel */}
                      <path
                        d="M21 7L21.5 8L27.5 24"
                        stroke="currentColor"
                        strokeWidth="1.2"
                        fill="none"
                        strokeLinecap="round"
                      />
                      {/* Tassel knot */}
                      <circle cx="21" cy="7" r="0.5" fill="currentColor" />
                    </svg>
                  </div>
                </div>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-2 lg:space-x-3">
            {navItems.map((item) =>
              item.to.startsWith("http") ? (
                <a
                  key={item.to}
                  href={item.to}
                  className="px-3 py-2 rounded-lg text-slate-600 hover:text-black font-bold hover:bg-white/30 transition-colors text-base flex items-center space-x-1.5"
                >
                  {item.icon}
                  <span>{item.label}</span>
                </a>
              ) : (
                <Link key={item.to} to={item.to}>
                  <button
                    className={`px-3 py-2 rounded-lg text-slate-600 hover:text-black font-bold hover:bg-white/30 transition-colors text-base flex items-center space-x-1.5 ${
                      item.isPremium ? "group premium-shimmer" : ""
                    }`}
                  >
                    {item.icon}
                    {item.isPremium ? (
                      <span className="relative">
                        {item.label}
                        <span className="absolute -top-1 -right-2 w-1.5 h-1.5 bg-gradient-to-r from-amber-400 to-yellow-300 rounded-full premium-sparkle"></span>
                      </span>
                    ) : (
                      <span>{item.label}</span>
                    )}
                  </button>
                </Link>
              )
            )}
          </div>

          {/* Desktop User Menu or Login Button */}
          <div className="hidden md:block pr-4">
            {userId ? (
              // User is logged in - show dropdown menu
              <Menu as="div" className="relative">
                <MenuButton className="flex items-center gap-2 p-2 rounded-full hover:bg-white/30 transition-colors">
                  <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {firstName?.charAt(0) ||
                        localStorage.getItem("firstName")?.charAt(0) ||
                        "U"}
                    </span>
                  </div>
                  <svg
                    className="w-4 h-4 text-slate-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </MenuButton>

                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <MenuItems className="absolute right-0 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900">
                        {firstName ||
                          localStorage.getItem("firstName") ||
                          "User"}
                      </p>
                      <p className="text-xs text-gray-500">
                        {localStorage.getItem("username") || "user@example.com"}
                      </p>
                    </div>

                    <MenuItem>
                      {({ active }) => (
                        <Link
                          to="/profile"
                          className={`${
                            active ? "bg-gray-100" : ""
                          } flex items-center px-4 py-2 text-sm text-gray-700`}
                        >
                          <svg
                            className="mr-3 h-5 w-5 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                            />
                          </svg>
                          Profile
                        </Link>
                      )}
                    </MenuItem>

                    <MenuItem>
                      {({ active }) => (
                        <Link
                          to="/settings"
                          className={`${
                            active ? "bg-gray-100" : ""
                          } flex items-center px-4 py-2 text-sm text-gray-700`}
                        >
                          <svg
                            className="mr-3 h-5 w-5 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                          </svg>
                          Settings
                        </Link>
                      )}
                    </MenuItem>

                    <div className="border-t border-gray-100">
                      <MenuItem>
                        {({ active }) => (
                          <button
                            onClick={handleLogout}
                            className={`${
                              active ? "bg-gray-100" : ""
                            } flex w-full items-center px-4 py-2 text-sm text-red-700`}
                          >
                            <svg
                              className="mr-3 h-5 w-5 text-red-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                              />
                            </svg>
                            Sign out
                          </button>
                        )}
                      </MenuItem>
                    </div>
                  </MenuItems>
                </Transition>
              </Menu>
            ) : (
              // User is not logged in - show login button
              <Link to="/login">
                <button className="group relative h-9 px-5 rounded-full bg-gradient-to-b from-indigo-400 to-indigo-500 hover:from-indigo-500 hover:to-indigo-600 transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5">
                  <div className="flex items-center space-x-2 text-white font-bold text-base">
                    <span>Login</span>
                    <svg
                      className="w-4 h-4 transform transition-transform duration-200 group-hover:translate-x-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M14 5l7 7m0 0l-7 7m7-7H3"
                      />
                    </svg>
                  </div>
                </button>
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden pr-4">
            <button
              ref={buttonRef}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-lg hover:bg-white/30 transition-colors"
            >
              {!isMenuOpen ? (
                <svg
                  className="w-6 h-6 text-slate-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              ) : (
                <svg
                  className="w-6 h-6 text-slate-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {isMenuOpen && (
          <div
            ref={menuRef}
            className="md:hidden mt-2 rounded-xl border border-slate-200 bg-slate-100/60 backdrop-blur-lg shadow-lg overflow-hidden"
          >
            <div className="p-2 space-y-1">
              {navItems.map((item) =>
                item.to.startsWith("http") ? (
                  <a
                    key={item.to}
                    href={item.to}
                    className="block w-full text-left px-4 py-2.5 text-base font-bold text-slate-600 hover:text-black hover:bg-white/30 rounded-lg transition-colors flex items-center space-x-2"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.icon}
                    <span>{item.label}</span>
                  </a>
                ) : (
                  <Link key={item.to} to={item.to} className="block">
                    <button
                      className={`w-full text-left px-4 py-2.5 text-base font-bold text-slate-600 hover:text-black hover:bg-white/30 rounded-lg transition-colors flex items-center space-x-2 ${
                        item.isPremium ? "group premium-shimmer" : ""
                      }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {item.icon}
                      {item.isPremium ? (
                        <span className="relative">
                          {item.label}
                          <span className="absolute -top-1 -right-1.5 w-1.5 h-1.5 bg-gradient-to-r from-amber-400 to-yellow-300 rounded-full premium-sparkle"></span>
                        </span>
                      ) : (
                        <span>{item.label}</span>
                      )}
                    </button>
                  </Link>
                )
              )}

              {/* Conditional Mobile User Menu or Login Button */}
              {userId ? (
                // User is logged in - show user menu options
                <div className="mt-2 pt-2 border-t border-slate-300">
                  <div className="px-4 py-2 mb-2">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
                        <span className="text-white text-sm font-medium">
                          {firstName?.charAt(0) ||
                            localStorage.getItem("firstName")?.charAt(0) ||
                            "U"}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-800">
                          {firstName ||
                            localStorage.getItem("firstName") ||
                            "User"}
                        </p>
                        <p className="text-xs text-slate-600">
                          {localStorage.getItem("username") ||
                            "user@example.com"}
                        </p>
                      </div>
                    </div>
                  </div>

                  <Link to="/profile" className="block">
                    <button
                      className="w-full text-left px-4 py-2.5 text-base font-bold text-slate-600 hover:text-black hover:bg-white/30 rounded-lg transition-colors flex items-center space-x-2"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                      <span>Profile</span>
                    </button>
                  </Link>

                  <Link to="/settings" className="block">
                    <button
                      className="w-full text-left px-4 py-2.5 text-base font-bold text-slate-600 hover:text-black hover:bg-white/30 rounded-lg transition-colors flex items-center space-x-2"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      <span>Settings</span>
                    </button>
                  </Link>

                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMenuOpen(false);
                    }}
                    className="w-full text-left px-4 py-2.5 text-base font-bold text-red-600 hover:text-red-800 hover:bg-white/30 rounded-lg transition-colors flex items-center space-x-2"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                      />
                    </svg>
                    <span>Sign out</span>
                  </button>
                </div>
              ) : (
                // User is not logged in - show login button
                <Link to="/login" className="block">
                  <button
                    className="w-full mt-2 px-4 py-2.5 rounded-lg bg-gradient-to-b from-indigo-400 to-indigo-500 text-white font-bold hover:from-indigo-500 hover:to-indigo-600 transition-all duration-200 flex items-center justify-center space-x-2"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <span>Login</span>
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M14 5l7 7m0 0l-7 7m7-7H3"
                      />
                    </svg>
                  </button>
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}

export default Navbar;
